import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Smartphone, Wrench, Gauge, ShieldCheck, Clock, Phone, Mail } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { LOGO, HERO_IMG, WORKSHOP_IMG, APP_URL, CONTACTS } from "@/lib/config";
import { ProductCard } from "@/components/site/ProductCard";
import { Stars } from "@/components/site/Stars";
import { Lightbox } from "@/components/site/Lightbox";

const SectionHead = ({ eyebrow, title, script, link, linkLabel, testid }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
    <div>
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h2 className="tms-title text-4xl sm:text-5xl lg:text-6xl">
        {title} {script && <span className="font-script normal-case text-red-500 font-bold">{script}</span>}
      </h2>
    </div>
    {link && (
      <Link to={link} className="btn-ghost text-sm" data-testid={testid}>{linkLabel} <ArrowRight size={16} /></Link>
    )}
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden grain" data-testid="home-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
    <div className="absolute inset-0 speed-lines opacity-40" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
      <div className="reveal">
        <span className="inline-block border border-red-600/60 text-red-500 text-[11px] uppercase tracking-[0.3em] px-3 py-1 mb-8">Officina ufficiale</span>
        <h1 className="tms-title text-6xl sm:text-7xl lg:text-8xl">
          Prestazioni<br />senza <span className="text-red-600">compromessi</span>
        </h1>
        <p className="font-script text-2xl sm:text-3xl text-zinc-200 mt-6">Passione per ogni dettaglio.</p>
        <p className="text-zinc-400 max-w-lg mt-6 leading-relaxed">Manutenzione, diagnosi elettronica e preparazione per la tua auto. Acquista il servizio online, presentati in officina con il tuo codice ordine.</p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/servizi" className="btn-red" data-testid="hero-services-button">Scopri i servizi <ArrowRight size={18} /></Link>
          {APP_URL ? (
            <a href={APP_URL} className="btn-ghost" data-testid="hero-app-button"><Smartphone size={18} /> Scarica l'app</a>
          ) : (
            <a href={CONTACTS.phoneHref} className="btn-ghost" data-testid="hero-call-button"><Phone size={18} /> Chiama {CONTACTS.phone}</a>
          )}
        </div>
      </div>
      <div className="relative reveal" style={{ animationDelay: "150ms" }}>
        <div className="absolute -inset-6 bg-red-600/20 blur-3xl" />
        <img src={LOGO} alt="TMS Garage" className="relative w-full max-w-md mx-auto shadow-[0_0_60px_rgba(220,38,38,0.25)] border border-red-900/40" data-testid="hero-logo" />
      </div>
    </div>
  </section>
);

const Strengths = () => (
  <section className="border-y border-zinc-900 bg-[#0D0D10]">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
      {[[Wrench, "Meccanica completa"], [Gauge, "Diagnosi elettronica"], [ShieldCheck, "Ricambi di qualità"], [Clock, "Tempi certi"]].map(([I, t]) => (
        <div key={t} className="flex items-center gap-3 py-6 px-2 border-zinc-900 [&:not(:last-child)]:lg:border-r">
          <I className="text-red-500 shrink-0" size={22} />
          <span className="font-display italic uppercase font-bold tracking-wider text-sm">{t}</span>
        </div>
      ))}
    </div>
  </section>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [lb, setLb] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/photos").then((r) => setPhotos(r.data));
    api.get("/reviews?limit=6").then((r) => setReviews(r.data));
  }, []);

  return (
    <>
      <Hero />
      <Strengths />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="home-services-section">
        <SectionHead eyebrow="Cosa facciamo" title="I nostri" script="servizi" link="/servizi" linkLabel="Vedi tutti" testid="home-all-services-link" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.slice(0, 3).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>

      <section className="bg-[#0D0D10] border-y border-zinc-900 py-24" data-testid="home-gallery-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHead eyebrow="Dall'officina" title="Lavori" script="recenti" link="/galleria" linkLabel="Galleria completa" testid="home-gallery-link" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {photos.slice(0, 6).map((ph, i) => (
              <button key={ph.id} onClick={() => setLb(i)} className={`group relative overflow-hidden ${i === 0 ? "md:col-span-2 md:row-span-2" : ""}`} data-testid={`home-gallery-photo-${i}`}>
                <img src={imgUrl(ph.url)} alt={ph.title} className="w-full h-full object-cover aspect-square transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-red-600/0 group-hover:bg-red-600/20 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="home-reviews-section">
        <SectionHead eyebrow="Dicono di noi" title="Recensioni" script="clienti" link="/recensisci" linkLabel="Lascia una recensione" testid="home-review-link" />
        {reviews.length === 0 ? (
          <p className="text-zinc-500 border border-dashed border-zinc-800 p-10 text-center" data-testid="home-no-reviews">Ancora nessuna recensione: acquista un servizio e sii il primo a lasciarne una.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <article key={r.id} className="bg-[#18181C] border-l-4 border-red-600 p-6" data-testid={`home-review-${r.id}`}>
                <Stars value={r.rating} size={16} />
                <p className="text-zinc-300 mt-4 leading-relaxed">“{r.comment}”</p>
                <p className="mt-5 font-display italic uppercase font-bold">{r.name}</p>
                <p className="text-xs text-zinc-500">{r.product_name}</p>
              </article>
            ))}
          </div>
        )}
      </section>

      <section id="contatti" className="relative border-t border-red-900/30" data-testid="home-contacts-section">
        <div className="grid lg:grid-cols-2">
          <img src={WORKSHOP_IMG} alt="Officina TMS Garage" className="w-full h-72 lg:h-full object-cover" />
          <div className="p-8 lg:p-16 bg-[#0D0D10]">
            <p className="eyebrow mb-3">Vieni a trovarci</p>
            <h2 className="tms-title text-5xl mb-8">Contatti <span className="font-script normal-case text-red-500">& orari</span></h2>
            <div className="space-y-3 text-zinc-300">
              {CONTACTS.address && <p data-testid="contacts-address">{CONTACTS.address}</p>}
              <a href={CONTACTS.phoneHref} className="flex items-center gap-3 hover:text-red-500 transition-colors" data-testid="contacts-phone"><Phone size={18} className="text-red-500" /> {CONTACTS.phone}</a>
              <a href={`mailto:${CONTACTS.email}`} className="flex items-center gap-3 hover:text-red-500 transition-colors" data-testid="contacts-email"><Mail size={18} className="text-red-500" /> {CONTACTS.email}</a>
            </div>
            <div className="red-rule my-8" />
            <dl className="space-y-3">
              {CONTACTS.hours.map(([d, h]) => (
                <div key={d} className="flex justify-between border-b border-zinc-800 pb-3 text-sm">
                  <dt className="font-display italic uppercase font-bold tracking-wider">{d}</dt>
                  <dd className="text-zinc-400">{h}</dd>
                </div>
              ))}
            </dl>
            {APP_URL ? (
              <a href={APP_URL} className="btn-red mt-10" data-testid="contacts-app-button"><Smartphone size={18} /> Apri l'app TMS Garage</a>
            ) : (
              <a href={CONTACTS.phoneHref} className="btn-red mt-10" data-testid="contacts-call-button"><Phone size={18} /> Chiama ora</a>
            )}
          </div>
        </div>
      </section>
      <Lightbox photos={photos} index={lb} setIndex={setLb} />
    </>
  );
}
